/**
 * src/components/auth/AccountModal.tsx
 * =============================================================================
 * Modal centrado de "Cuenta y facturación". Aparece encima de la pizarra
 * sin sacar al usuario a una página externa (Polar Customer Portal).
 *
 * Diseño (2026-05-27): consistente con la X de /precios — backdrop muy
 * suave (sin blur), card centrado con `rounded-2xl shadow-2xl`, X arriba
 * derecha. Cierra con Escape o click en el backdrop.
 *
 * Contenido:
 *   - Plan actual (Pro / Free) + barra de almacenamiento.
 *   - Lista de suscripciones activas con sus precios + fecha próximo cobro.
 *   - Botón "Cancelar" en cada una → confirm inline → llama al Worker.
 *   - Link discreto "¿Tarjeta caducada? Gestiona el pago en Polar" como
 *     fallback (única ruta donde sí abrimos Polar, por PCI compliance).
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X, Loader2 } from 'lucide-react'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import {
  cancelSubscription,
  getPortalUrl,
  getQuota,
  getSubscriptions,
  formatBytes,
  BillingError,
  type QuotaInfo,
  type SubscriptionRow,
} from '@/services/billingService'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'

interface Props {
  open: boolean
  onClose: () => void
}

export function AccountModal({ open, onClose }: Props) {
  const { t } = useTranslation()
  const { activeSpaceId } = useActiveSpace()
  const [quota, setQuota] = useState<QuotaInfo | null>(null)
  const [subs, setSubs] = useState<SubscriptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [cancelingId, setCancelingId] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, open)

  /**
   * Carga quota + subscriptions en paralelo. Lo usamos al abrir el modal
   * y tras cancelar una suscripción para refrescar el estado.
   *
   * Patrón usado: setState dentro de `.then` (async) — pasa el lint
   * `react-hooks/set-state-in-effect` que prohíbe setState síncronos
   * en effects pero permite los asíncronos dentro de promesas.
   */
  const load = useCallback((onDone?: () => void) => {
    let cancelled = false
    void Promise.all([
      getQuota(activeSpaceId ?? undefined).catch(() => null),
      getSubscriptions().catch(() => [] as SubscriptionRow[]),
    ]).then(([q, s]) => {
      if (cancelled) return
      setQuota(q)
      setSubs(s)
      setLoading(false)
      onDone?.()
    })
    return () => {
      cancelled = true
    }
  }, [activeSpaceId])

  useEffect(() => {
    if (!open) return undefined
    return load()
  }, [open, load])

  // Escape cierra. El backdrop maneja click-fuera (más fiable que listeners
  // a nivel document para modales).
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const onConfirmCancel = useCallback(
    async (subId: string) => {
      setCancelingId(subId)
      setCancelError(null)
      try {
        await cancelSubscription(subId, { immediate: false })
        load(() => {
          setCancelingId(null)
          setConfirmId(null)
        })
      } catch (err) {
        console.error('cancel failed', err)
        setCancelError(err instanceof BillingError ? err.code : 'cancel_failed')
        setCancelingId(null)
        setConfirmId(null)
      }
    },
    [load],
  )

  if (!open) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('cuenta.etiqueta')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center p-4"
    >
      {/* `dvh` (dynamic viewport height) en lugar de `vh` para que Safari
          mobile no nos corte el modal cuando aparece la address bar. */}
      <div className="w-full max-w-lg max-h-[85dvh] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 h-14 border-b border-zinc-100 shrink-0">
          <h2 className="text-base font-semibold text-zinc-900">{t('cuenta.titulo')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="h-8 w-8 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
          >
            <X className="h-5 w-5" strokeWidth={1.8} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            </div>
          ) : (
            <>
              {/* Plan + storage */}
              {quota && (
                <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <p className="text-sm font-medium text-zinc-900">
                      {t('cuenta.plan')}{' '}
                      <span className="text-zinc-500 font-normal">
                        · {quota.plan === 'pro' ? t('panel.planPro') : t('panel.planGratis')}
                      </span>
                    </p>
                    <p className="text-xs text-zinc-500">
                      {formatBytes(quota.bytesUsed)} / {formatBytes(quota.bytesLimit)}
                    </p>
                  </div>
                  <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-zinc-900 transition-[width] duration-300"
                      style={{
                        width: `${Math.min(100, (quota.bytesUsed / Math.max(1, quota.bytesLimit)) * 100)}%`,
                      }}
                    />
                  </div>
                  {quota.plan === 'free' && (
                    <p className="text-xs text-zinc-500">
                      {t('panel.caducaGratis')}{' '}
                      <Link
                        to="/precios"
                        onClick={onClose}
                        className="font-medium text-zinc-900 underline underline-offset-4"
                      >
                        {t('panel.ctaPro')}
                      </Link>
                    </p>
                  )}
                </section>
              )}

              {/* Suscripciones */}
              <section>
                <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
                  {t('cuenta.suscripciones')}
                </h3>
                {cancelError && (
                  <p className="mb-2 text-xs text-red-600" role="alert">
                    {cancelError === 'active_spaces_require_pro'
                      ? t('cuenta.errores.active_spaces_require_pro')
                      : t('cuenta.errores.generic')}
                  </p>
                )}

                {subs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center">
                    <p className="text-sm text-zinc-600">{t('cuenta.sinSuscripciones')}</p>
                    <Link
                      to="/precios"
                      onClick={onClose}
                      className="inline-block mt-2 text-sm text-zinc-900 underline underline-offset-4"
                    >
                      {t('actions.verPlanes')}
                    </Link>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {subs.map((s) => (
                      <SubRow
                        key={s.polarSubscriptionId}
                        sub={s}
                        canceling={cancelingId === s.polarSubscriptionId}
                        confirming={confirmId === s.polarSubscriptionId}
                        onAskConfirm={() => setConfirmId(s.polarSubscriptionId)}
                        onCancelConfirm={() => setConfirmId(null)}
                        onConfirm={() => void onConfirmCancel(s.polarSubscriptionId)}
                      />
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 shrink-0">
          <p className="text-[11px] text-zinc-500 text-center leading-snug">
            {t('cuenta.pie')}
          </p>
        </footer>
      </div>
    </div>
  )
}

interface SubRowProps {
  sub: SubscriptionRow
  canceling: boolean
  confirming: boolean
  onAskConfirm: () => void
  onCancelConfirm: () => void
  onConfirm: () => void
}

function SubRow({ sub, canceling, confirming, onAskConfirm, onCancelConfirm, onConfirm }: SubRowProps) {
  // `i18n` además de `t` para formatear la fecha en el idioma activo: antes
  // iba fija a 'es-ES' y un usuario alemán veía la fecha en formato español.
  const { t, i18n } = useTranslation()
  // Un plan por entrada, no un ternario: cuando existían solo dos planes, el
  // `else` se comía cualquier otro y una suscripción de espacio se habría
  // mostrado como "+GB de almacenamiento" al precio equivocado. En la pantalla
  // donde el cliente revisa lo que le cobramos, eso es de lo peor que puede
  // pasar. El `?? sub.plan` deja ver el identificador crudo si algún día
  // aparece un plan nuevo sin pasar por aquí: raro, pero honesto.
  const PLANES: Record<string, { label: string; price: string }> = {
    pro: { label: t('cuenta.planes.proLabel'), price: t('cuenta.planes.proPrecio') },
    space_base: { label: t('cuenta.planes.espacioLabel'), price: t('cuenta.planes.espacioPrecio') },
    storage_addon: { label: t('cuenta.planes.addonLabel'), price: t('cuenta.planes.addonPrecio') },
  }
  const info = PLANES[sub.plan]
  const label = info?.label ?? sub.plan
  const price = info?.price ?? '—'
  const nextDate = sub.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString(i18n.language)
    : '—'
  const status = sub.cancelAtPeriodEnd
    ? t('cuenta.cancelacionProgramada', { fecha: nextDate })
    : t('cuenta.proximoCobro', { fecha: nextDate })

  // Enlace al Customer Portal de Polar (tarjeta y facturas). Se abre en una
  // pestaña nueva porque es un dominio externo y el usuario debe volver aquí.
  // Si el backend no puede emitir la sesión (scope de Polar sin activar), el
  // enlace simplemente no navega y se muestra el error genérico del modal.
  const [openingPortal, setOpeningPortal] = useState(false)
  const [portalError, setPortalError] = useState(false)
  async function onManagePayment() {
    setOpeningPortal(true)
    setPortalError(false)
    try {
      const url = await getPortalUrl(sub.polarSubscriptionId)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      setPortalError(true)
    } finally {
      setOpeningPortal(false)
    }
  }

  return (
    <li className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-900">
            {label} <span className="text-zinc-400 font-normal">· {price}</span>
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">{status}</p>
          <button
            type="button"
            onClick={onManagePayment}
            disabled={openingPortal}
            className="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-800 mt-1 disabled:opacity-50"
          >
            {openingPortal ? t('cuenta.abriendoPortal') : t('cuenta.gestionarPago')}
          </button>
          {portalError && (
            <p className="text-xs text-red-600 mt-1" role="alert">{t('cuenta.portalError')}</p>
          )}
        </div>

        {sub.cancelAtPeriodEnd ? (
          <span className="text-xs text-zinc-400 shrink-0">{t('cuenta.pendienteCierre')}</span>
        ) : confirming ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={onCancelConfirm}
              disabled={canceling}
              className="text-xs px-2.5 h-7 rounded-md text-zinc-700 hover:bg-zinc-100"
            >
              {t('panel.no')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={canceling}
              className="text-xs px-2.5 h-7 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {canceling && <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />}
              {t('cuenta.siCancelar')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onAskConfirm}
            className="text-xs px-2.5 h-7 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 shrink-0"
          >
            {t('actions.cancelar')}
          </button>
        )}
      </div>
    </li>
  )
}
